import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, LogOut } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import logo from "@/assets/logo.png";

const SITE_PASSWORD = "ly123*";
const AUTH_KEY = "lttp_site_auth";

const HomePage = () => {
  const [showMap, setShowMap] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [password, setPassword] = useState("");
  const [isAuthed, setIsAuthed] = useState(false);

  useEffect(() => {
    setIsAuthed(localStorage.getItem(AUTH_KEY) === "1");
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === SITE_PASSWORD) {
      localStorage.setItem(AUTH_KEY, "1");
      setIsAuthed(true);
      setShowLogin(false);
      setPassword("");
      toast({ title: "Signed in", description: "Welcome back." });
    } else {
      toast({ title: "Incorrect password", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(AUTH_KEY);
    setIsAuthed(false);
    toast({ title: "Signed out" });
  };

  const address = "124 Williams St, Lockland, Ohio 45215";
  const encoded = encodeURIComponent(address);
  const embedUrl = `https://www.google.com/maps?q=${encoded}&output=embed`;
  const gmapsUrl = `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  const appleUrl = `https://maps.apple.com/?daddr=${encoded}`;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-md flex flex-col items-center text-center space-y-8">
          <img
            src={logo}
            alt="Lynx to the Past"
            className="w-full max-w-sm"
          />

          <div className="space-y-3">
            <button
              onClick={() => setShowMap(true)}
              aria-label={`Get directions to ${address}`}
              className="text-sm font-mono text-accent font-semibold hover:underline"
            >
              📍 {address}
            </button>
            <p className="text-[10px] font-mono text-muted-foreground tracking-[0.3em] uppercase">
              Est. 2024
            </p>
          </div>

          {isAuthed ? (
            <Button
              variant="outline"
              onClick={handleLogout}
              className="font-mono"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          ) : (
            <Button
              onClick={() => setShowLogin(true)}
              className="font-mono"
            >
              <Lock className="w-4 h-4 mr-2" />
              Log In
            </Button>
          )}
        </div>
      </main>

      <footer className="text-center pb-4 px-5">
        <p className="text-[10px] font-mono text-muted-foreground">
          © 2026 LYNX TO THE PAST • ALL RIGHTS RESERVED
        </p>
      </footer>

      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">Enter Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-3">
            <Input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="font-mono"
            />
            <Button type="submit" className="w-full font-mono">
              Sign In
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showMap} onOpenChange={setShowMap}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">Directions to {address}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full overflow-hidden rounded-lg border border-border">
            <iframe
              title="Store location"
              src={embedUrl}
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <a
              href={gmapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-center text-xs font-mono font-semibold py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90"
            >
              Google Maps
            </a>
            <a
              href={appleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-center text-xs font-mono font-semibold py-2 rounded-lg bg-accent text-accent-foreground hover:opacity-90"
            >
              Apple Maps
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
